/**
 * Formatting Utilities
 *
 * Functions for formatting currencies, dates, and other display values.
 */

import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { Money } from '../types';

/**
 * Parse a Money string (backend Decimal serialized as JSON string) to a number.
 * Returns 0 for null, undefined, or non-numeric values to prevent NaN propagation.
 */
export function parseMoney(value: Money | null | undefined): number {
  if (value == null) return 0;
  const num = parseFloat(value);
  return Number.isNaN(num) ? 0 : num;
}

/**
 * Parse a Money string (decimal dollars) to integer cents.
 * Returns 0 for null, undefined, or empty values.
 */
export function parseMoneyToCents(money: Money): number {
  return Math.round(parseMoney(money) * 100);
}

/**
 * Format a Money string (decimal) as currency.
 */
export function formatMoney(value: Money): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseMoney(value));
}

/**
 * Format a number of cents as a currency string.
 *
 * @param cents - Amount in cents (e.g., 1500 = $15.00)
 * @param currency - Currency code (default: USD)
 * @returns Formatted currency string (e.g., "$15.00")
 */
export function formatCurrency(cents: number, currency = 'USD'): string {
  const amount = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(amount);
}

/**
 * Format a number of cents with color indication for positive/negative.
 *
 * @param cents - Amount in cents
 * @returns Object with formatted string and color class
 */
export function formatAmount(cents: number): {
  formatted: string;
  className: string;
} {
  const formatted = formatCurrency(Math.abs(cents));
  const sign = cents >= 0 ? '+' : '-';
  const className = cents > 0 ? 'amount-positive' : cents < 0 ? 'amount-negative' : 'amount-neutral';

  return {
    formatted: cents === 0 ? formatted : `${sign}${formatted}`,
    className
  };
}

/**
 * Format a date for display.
 *
 * @param date - Date string or Date object
 * @param pattern - date-fns format pattern (default: 'MMM d, yyyy')
 * @returns Formatted date string
 */
export function formatDate(date: string | Date, pattern = 'MMM d, yyyy'): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, pattern);
}

/**
 * Format a date as a relative time string.
 *
 * @param date - Date string or Date object
 * @returns Relative time string (e.g., "2 days ago")
 */
export function formatRelativeDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

/**
 * Format a percentage.
 *
 * @param value - Decimal value (e.g., 0.156 = 15.6%)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export function formatPercent(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Convert a Money string (decimal dollars) to integer cents.
 * Returns 0 for null, undefined, or empty values.
 */
export function toCents(value: string | null | undefined): number {
  if (value == null || value === '') return 0;
  const num = parseFloat(value);
  return Number.isNaN(num) ? 0 : Math.round(num * 100);
}

/**
 * Format a large number with abbreviations.
 *
 * @param num - Number to format
 * @returns Abbreviated string (e.g., "1.2K", "3.5M")
 */
export function formatCompactNumber(num: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short'
  }).format(num);
}

/**
 * Format days until due as a human-readable string.
 */
export function formatDaysUntil(daysUntil: number, isOverdue: boolean): string {
  if (isOverdue) {
    const days = Math.abs(daysUntil);
    return days === 1 ? '1 day overdue' : `${days} days overdue`;
  }
  if (daysUntil === 0) return 'Due today';
  if (daysUntil === 1) return 'Due tomorrow';
  if (daysUntil <= 7) return `Due in ${daysUntil} days`;
  if (daysUntil <= 14) return 'Due in ~1 week';
  if (daysUntil <= 21) return 'Due in ~2 weeks';
  return `Due in ${Math.ceil(daysUntil / 7)} weeks`;
}

/**
 * Format mask for display
 */
export function formatMask(mask: string | null): string {
  if (!mask) return '';
  return `••••${mask}`;
}

/**
 *
 * @param days - Number of days late or early
 * @returns Formatted drift string (e.g., "2 days early" / "1 day late")
 */
export function formatDrift(days: number | null): string {
  if (days == null || days === 0) return 'on time';
  return days > 0 ? `+${days}d late` : `${days}d early`;
}

/**
 * Format a deviation amount.
 *
 * @param amount - Deviation amount
 * @returns Formatted deviation string (e.g., "+$5.00" / "-$2.50")
 */
export function formatDeviation(amount: string): string {
  const num = parseFloat(amount);
  if (num === 0) return 'exact';
  return formatMoney(amount);
}

/** Convert a hex color (e.g. "#ff8800") to an rgba string with the given alpha. */
export function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
